// articles/StrokePlaySetup.js
// Event setup guide #3 (Sept 28, 2026): an individual Stroke Play event in TTO,
// start to finish, in the order the steps have to happen. Every player plays his
// own ball and the lowest total wins — net, gross or both. Written from the live
// screens (App.js StrokePlayGamePanel, the Print tab, the scorecard codes) — button
// and tab names are quoted exactly as they appear, so if a label changes in App.js,
// change it here too. Same shared parts as the Captain's Choice and Best Ball guides.
//
// Linked from inside the app: the guides chooser (📖 Guides → Setting up Events /
// Tournaments), the Event setup guides buttons, and the Stroke Play panel in Games
// Setup. Public, so prospective organizers can read it before they sign up.

import React from 'react';
import { colors, getGuideBySlug } from '../guidesData';
import { H2, H3, P, UL, LI, GuideCTA, FAQ } from '../articleUI';
import { Step, Btn, Heads, StepTracker, PRINT_CSS, CtpSection } from './eventGuideParts';

const guide = getGuideBySlug('how-to-set-up-a-stroke-play-golf-tournament');

const FAQS = [
  {
    q: 'Can I rank the field on net and gross at the same time?',
    a: 'Yes. Choose Both. TTO ranks a net competition and a gross competition from the same cards, and a player can place in both. With a buy-in, you say how much of each player’s buy-in goes to the gross purse; the rest goes to net.',
  },
  {
    q: 'What handicap does each player get?',
    a: 'His playing handicap: the allowance you set (95% by default, the USGA recommendation) applied to his course handicap from his own tee, rounded once. His strokes fall on the hardest holes of his own tee. You can also set a player’s handicap for this event only with Event handicaps.',
  },
  {
    q: 'Why did four players get paid when I pay three places?',
    a: 'Because of a tie and the Tied scores share the money choice. Players on the same score split the money for the places they occupy, so two players tied for 3rd share 3rd and 4th place money. Choose Pay by the decided order if you want exactly your paid places, in countback order.',
  },
  {
    q: 'Do Stroke Play scores post to GHIN?',
    a: 'Yes. Every player plays his own ball, so the round is acceptable for handicap purposes. Players post their own scores from their own TTO link or on ghin.com; TTO never posts for them.',
  },
  {
    q: 'Can I run Stroke Play and Best Ball on the same round?',
    a: 'Yes. The same cards can feed an individual Stroke Play game and a Best Ball team game. A round has one Stroke Play game, which can rank net, gross or both.',
  },
  {
    q: 'Can I pay the front nine and back nine separately?',
    a: 'Yes. In What it pays, choose Front & back, or Front, back & overall. Each part is its own competition with its own share of the buy-in and its own paid places. For example, a $10 buy-in split $5 / $5 and paying 75% / 25% on each nine gives a 20-player field $75 and $25 on the front and the same on the back. Each player keeps his 18-hole strokes where they fall, the game’s standings get Front 9 / Back 9 / Overall tabs, and the statement lists each part’s winners with one total per player. It needs an 18-hole round ranked on Net or Gross, not Both.',
  },
  {
    q: 'Can I split the field into flights, or add up two days?',
    a: 'Not yet. Today a Stroke Play event is one field over one round, ranked net, gross or both. Flights and multi-round events are planned.',
  },
];

export default function StrokePlaySetup() {
  return (
    <article>
      <h1
        style={{
          fontFamily: "'DM Serif Display', Georgia, serif",
          fontSize: 38,
          fontWeight: 400,
          color: colors.gray900,
          margin: '0 0 8px',
          lineHeight: 1.15,
        }}
      >
        How to Set Up a Stroke Play Golf Tournament
      </h1>
      <style>{PRINT_CSS}</style>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', margin: '0 0 28px' }}
      >
        <p style={{ fontSize: 15, color: colors.gray400, margin: 0 }}>
          {guide.readMinutes} min read &middot; Event setup guide
        </p>
        <button
          type="button"
          className="guide-no-print"
          onClick={() => window.print()}
          style={{
            background: 'white',
            color: colors.green800,
            border: `1px solid ${colors.gray300}`,
            borderRadius: 8,
            padding: '8px 14px',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          🖨️ Print this guide
        </button>
      </div>

      <P>
        In a stroke play tournament every player plays his own ball, counts every stroke, and the
        lowest total wins: on net, on gross, or both. This guide walks you through setting up a
        stroke play golf tournament in Tee Time Organizer, from the first click to the final
        results, in the order the steps need to happen.
      </P>

      <StepTracker
        storageKey="tto_guide_stroke_play_steps"
        steps={[
          'Create the event',
          'Set up the event',
          'Invite players',
          'Starting holes & groups',
          'Stroke Play game',
          'Handicaps for the day',
          'Codes, cards & signs',
          'Play & leaderboard',
          'Cards, ties & finalize',
        ]}
        note="Steps 1 to 3 can be done weeks ahead; steps 4 to 7 wait until the field is set. Tap a step to jump to it."
      />

      <H2>Before you start</H2>
      <P>Settle these decisions first. Each one is entered in a later step, but you&rsquo;ll need the answers there.</P>
      <UL>
        <LI>
          <strong>Each event needs an Event Pass</strong> (unless events are included in your
          plan). Where the <Btn>This is an event</Btn> checkbox would be, you&rsquo;ll see{' '}
          <Btn>🔒 This is an event · Get an Event Pass</Btn>. Buy the pass there and the checkbox
          opens; the pass also switches on scoring and the event games. Running your very first
          event? Start with{' '}
          <a href="/guides/how-to-run-a-golf-tournament-with-tee-time-organizer" style={{ color: colors.green700 }}>
            Setting Up Your Tournament
          </a>.
        </LI>
        <LI>
          <strong>Who is invited:</strong> your whole player list, or only the players you choose
          (step 3).
        </LI>
        <LI>
          <strong>Shotgun or tee times</strong>, and for a shotgun, how many groups start on each hole
          (step 4).
        </LI>
        <LI>
          <strong>Net, gross or both</strong>, and the handicap allowance (step 5).
        </LI>
        <LI>
          <strong>Money:</strong> whether TTO settles a buy-in or you pay prizes yourself, how many
          places you pay, how money is split on a tie, and how ties are broken. TTO decides ties only
          for the places you pay, so it needs this before scores come in (step 5).
        </LI>
      </UL>

      <H2>Setup, step by step</H2>

      <Step n={1} title="Create the tee time and mark it as an event" when="Any time before registration opens">
        <UL>
          <LI>
            On your dashboard, in <Btn>Create Tee Time</Btn>, fill in the date, time, course and
            slots.
          </LI>
          <LI>
            <strong>Start Type:</strong> choose <Btn>Shotgun</Btn> if everyone starts at once. You
            assign the starting holes later, on the Pairings page (step 4).
          </LI>
          <LI>
            <strong>Inviting only selected players?</strong> Tick <Btn>Private Tee Time</Btn> (see
            step 3 for why).
          </LI>
          <LI>
            Tick <Btn>This is an event</Btn> and click <Btn>Add Tee Time</Btn>. The checkbox appears
            for single tee times, not recurring or bulk ones.
          </LI>
          <LI>
            The <Btn>Set up event</Btn> window opens on its own. For an existing tee time, use{' '}
            <Btn>✏️ Edit</Btn>, tick <Btn>This is an event</Btn>, then <Btn>Set up event ▸</Btn>.
          </LI>
        </UL>
      </Step>

      <Step n={2} title="Set up the event" when="Right after step 1">
        <UL>
          <LI>
            <strong>Event name</strong> (the only required field). It becomes the title on the
            leaderboard, scorecards and cart signs.
          </LI>
          <LI>
            <strong>Live leaderboard address</strong>: made from the name; shorten it if you like.
            It&rsquo;s printed on the cards, so settle it before you print.
          </LI>
          <LI>
            <strong>Event logo</strong> (optional): shown on the leaderboard, scorecards and cart
            signs.
          </LI>
          <LI>
            <strong>Scoring</strong>: leave it on <Btn>Each player enters their own score</Btn>.
            Stroke play needs every player&rsquo;s own card.
          </LI>
          <LI>
            <strong>GHIN posting eligible</strong>: leave it on <strong>Yes</strong>. Each player
            plays his own ball, so players can post their rounds.
          </LI>
          <LI>
            Click <Btn>Save event</Btn>. If you came in through <Btn>✏️ Edit</Btn>, also click{' '}
            <Btn>Save Changes</Btn> on the tee time.
          </LI>
        </UL>
        <Heads>
          <strong>Money, places paid and tie-breaking are not on this screen.</strong> You set them
          with the game in step 5. Have your payout plan decided by then.
        </Heads>
      </Step>

      <Step n={3} title="Invite players" when="When you're ready to open registration">
        <P>
          <Btn>📣 Publish</Btn> on the tee time card makes the event live. How you publish decides who
          hears about it.
        </P>
        <H3>Option A: invite your whole player list</H3>
        <UL>
          <LI>
            <Btn>📣 Publish</Btn> sends a notice to every player on your list, and the event appears in
            each player&rsquo;s app, where they can RSVP.
          </LI>
          <LI>
            Choosing <Btn>Send No Notifications</Btn> skips the notice, but the event still appears in
            your players&rsquo; apps and they can still RSVP.
          </LI>
        </UL>
        <H3>Option B: invite only the players you choose (recommended)</H3>
        <UL>
          <LI>
            Make sure the tee time is marked <Btn>Private Tee Time</Btn>. A private event does not
            appear in your players&rsquo; apps unless they have been invited and have accepted the
            invitation to play. Everyone playing sees it; the rest of your list doesn&rsquo;t.
          </LI>
          <LI>
            <Btn>📣 Publish</Btn> it with <Btn>Send No Notifications</Btn>, which gives{' '}
            <Btn>Publish (No Notifications)</Btn>.
          </LI>
          <LI>
            Click <Btn>🎟️ Invite</Btn> on the tee time card and copy the invite link.
          </LI>
          <LI>
            Put the link in an invitation email you write yourself and send it to the players you
            want.
          </LI>
          <LI>
            A player who opens the link is registered. Anyone who isn&rsquo;t already in your TTO
            player list is added as a guest.
          </LI>
        </UL>
      </Step>

      <Step n={4} title="Set the starting holes and build the groups" when="Once the field is set, on the Pairings page">
        <P>
          In stroke play the groups are only who plays together; they don&rsquo;t affect the
          results. But each saved group gets one printed scorecard and one scorecard code, so save
          the pairings before you print.
        </P>
        <H3>4a. Set up the shotgun (skip for a tee-times start)</H3>
        <UL>
          <LI>
            Open the <strong>Pairings</strong> page for the event and turn on{' '}
            <Btn>🎯 Shotgun Start</Btn>. The <strong>⚡ Shotgun Setup</strong> window opens. Later
            you can reopen it with <Btn>⚡ Edit Setup</Btn>.
          </LI>
          <LI>
            <strong>Group size:</strong> usually 4.
          </LI>
          <LI>
            <strong>Presets</strong> fill in the list of starting holes:
            <UL>
              <LI>
                <Btn>Standard (18)</Btn>: one group on every hole. Use it for a full field.
              </LI>
              <LI>
                <Btn>Modified (9)</Btn>: one group on each of holes 1 to 9.
              </LI>
              <LI>
                <Btn>Fit to players</Btn>: only as many groups as your field needs, filled from hole
                1 and then backwards (18, 17, 16 …). This is the usual choice for a partial field.
              </LI>
            </UL>
          </LI>
          <LI>
            <strong>Adjust by hand:</strong> use <strong>+</strong> and <strong>−</strong> on a hole
            to change its number of groups (two on one hole start as A and B).{' '}
            <strong>✕</strong> removes a hole and <Btn>+ Add Hole</Btn> puts one back.
          </LI>
          <LI>
            Check the capacity line. It should read <strong>✓ Fits N players</strong>, not{' '}
            <strong>over capacity</strong>. Then click <Btn>Create N Groups</Btn>.
          </LI>
        </UL>
        <H3>4b. Fill the groups</H3>
        <UL>
          <LI>
            <strong>Shotgun:</strong> use the fill buttons in the green{' '}
            <strong>Shotgun Structure</strong> bar: <Btn>⚖️ Balanced</Btn>, <Btn>🎲 Random</Btn> or{' '}
            <Btn>⛳ HCP</Btn> (similar handicaps together).
          </LI>
          <LI>
            <strong>Tee-times start:</strong> use <Btn>⚖️ Balanced Flights ▾</Btn>, or move players
            into groups by hand.
          </LI>
          <LI>
            Click <Btn>💾 Save Pairings</Btn> to save the groups and show the pairings in each
            player&rsquo;s app. Use <Btn>Save without sharing</Btn> to hold them back, for example
            until a reception the night before.
          </LI>
        </UL>
      </Step>

      <Step n={5} title="Add the Stroke Play game" when="After the field is set">
        <Heads>
          <strong>Every player needs a complete profile before the round.</strong> Each player&rsquo;s
          profile must include a <strong>preferred tee</strong> and a <strong>Handicap Index</strong>,
          so that his course handicap can be determined. Check them in Admin &rarr; Players &rarr;
          edit each player, including guests added from your invite link. In a net game, a player
          with no Handicap Index plays as scratch; TTO names any such player when you save the games.
        </Heads>
        <UL>
          <LI>
            Click <Btn>🎮 Games</Btn> on the tee time card and tick <strong>Stroke Play</strong>. A
            round has one Stroke Play game.
          </LI>
          <LI>
            <strong>Rank on:</strong> <Btn>Net</Btn>, <Btn>Gross</Btn> or <Btn>Both</Btn>. With Both,
            TTO ranks a net and a gross competition from the same cards, and a player can place in
            both.
          </LI>
          <LI>
            <strong>Handicap allowance:</strong> 95% by default, the USGA recommendation for
            individual stroke play (a small group may prefer 100%). It&rsquo;s applied to each
            player&rsquo;s course handicap and rounded once, and his strokes fall on the hardest holes
            of his own tee. Change it if your event uses a different one;{' '}
            <strong>Reset to 95</strong> puts it back.
          </LI>
          <LI>
            <strong>What it pays:</strong> <Btn>Whole round</Btn>, <Btn>Front &amp; back</Btn> or{' '}
            <Btn>Front, back &amp; overall</Btn>. With the nines paid separately, each part is its own
            competition with its own places and money: say how the buy-in is split between the parts
            and, for paid places, the percentages for each. The window shows what each place pays for
            today&rsquo;s field (for example $16 split $8 / $8, 75% / 25%, 20 players: $120 and $40 on
            each nine). Each player keeps his 18-hole
            strokes where they fall, and the game&rsquo;s standings in the app get Front 9 / Back 9 / Overall tabs. It needs
            an 18-hole round ranked on Net or Gross (not Both).
          </LI>
          <LI>
            <strong>Payouts:</strong>
            <UL>
              <LI>
                <strong>Handled outside TTO</strong> (the default for an event): TTO names the places
                and the club pays from its own prize table. Set <strong>Places decided</strong> to the
                number of places you award.
              </LI>
              <LI>
                <strong>Winner takes all:</strong> every buy-in goes to first place, split on a tie.
              </LI>
              <LI>
                <strong>Paid places:</strong> enter the <strong>Buy-in</strong> per player, choose{' '}
                <strong>Places paid</strong>, and set each place&rsquo;s percentage (for example 50 /
                30 / 20). They must add up to 100%.
              </LI>
              <LI>
                <strong>Ranking on Both with a buy-in?</strong> Say how much of each buy-in goes to
                the gross purse; the rest goes to net.
              </LI>
              <LI>
                <strong>Keeping part of the entry as a fee?</strong> TTO pays out the whole buy-in,
                so choose <strong>Handled outside TTO</strong> and pay the prizes yourself.
              </LI>
            </UL>
          </LI>
          <LI>
            <strong>Money on a tie:</strong>
            <UL>
              <LI>
                <strong>Tied scores share the money:</strong> players on the same score split the
                money for the places they occupy. Two players tied for 3rd share 3rd and 4th place
                money, whatever the countback says about the trophy, so more players than places can
                be paid.
              </LI>
              <LI>
                <strong>Pay by the decided order:</strong> the tie-break below decides the places,
                and each place is paid its own share. A tie it can&rsquo;t split still shares.
              </LI>
            </UL>
          </LI>
          <LI>
            <strong>How ties are decided:</strong>
            <UL>
              <LI>
                <strong>Scorecard playoff (USGA):</strong> best score over the last 9 holes, then the
                last 6, the last 3, then the 18th. On net, that share of the playing handicap is
                taken off.
              </LI>
              <LI>
                <strong>Hole handicap:</strong> compare the cards hole by hole, hardest hole first;
                the first better score wins.
              </LI>
              <LI>
                <strong>We&rsquo;ll decide:</strong> tied players stay tied until you put them in
                order, for example after a real playoff.
              </LI>
            </UL>
            Only ties for the places you pay (or decide) are broken, and only once everyone in the
            tie has finished. Other ties keep their T.
          </LI>
          <LI>
            Click <Btn>Save Games</Btn>.
          </LI>
        </UL>
        <CtpSection />
      </Step>

      <Step n={6} title="Adjust handicaps for the day (optional)" when="Any time before or during the round">
        <UL>
          <LI>
            If the committee sets a player&rsquo;s handicap for this event only, use{' '}
            <strong>⚖️ Event handicaps</strong> in Admin &rarr; <strong>⛳ Scoring</strong> &rarr;{' '}
            <strong>Post-Round Score Entry</strong>. The player&rsquo;s club record and GHIN are not
            changed.
          </LI>
          <LI>
            A change made during the round re-scores the holes he has already played, so the
            leaderboard may move.
          </LI>
        </UL>
      </Step>

      <Step n={7} title="Issue scorecard codes, then print cards and signs" when="The day before or the morning of">
        <UL>
          <LI>
            <strong>Scorecard codes first:</strong> in <Btn>🖨️ Print</Btn>, open{' '}
            <Btn>Scorecard codes…</Btn> and click <Btn>Issue codes</Btn>. Each group gets a six-digit
            code and a matching QR code. A scorecard only carries its code if the code existed when
            the card was printed.
          </LI>
          <LI>
            Then <Btn>Print scorecards</Btn> (one card per group, one row per player) and{' '}
            <Btn>Print cart signs</Btn>. The tee sheet, event sign, posting sheets and desk sheet are
            there too.
          </LI>
          <LI>
            On a net game each player&rsquo;s row shows his playing handicap (&ldquo;PH&rdquo;) and an
            asterisk on every hole where he gets a stroke.
          </LI>
          <LI>
            If you move players between groups after printing, reprint their cards.
          </LI>
        </UL>
      </Step>

      <H2>On the day</H2>

      <Step n={8} title="Play and follow the leaderboard" when="During the round">
        <H3>Keeping score</H3>
        <UL>
          <LI>
            Players in your TTO player list get a link to their TTO app in their reminder email. Each
            player enters his own score on each hole.
          </LI>
          <LI>
            <strong>No link handy, or a guest?</strong> He points his phone&rsquo;s camera at the QR
            code on his group&rsquo;s scorecard or cart sign, taps his own name, and his card opens.
            No app, password or sign-in is needed. Posting to GHIN still needs his own TTO link or
            ghin.com.
          </LI>
          <LI>
            One player can keep score for his whole group with <strong>+ Score for another player</strong>.
          </LI>
          <LI>
            <strong>Picked up?</strong> A player taps <strong>X</strong>. It counts as a net double
            bogey and shows as &ldquo;7x&rdquo;.
          </LI>
        </UL>

        <H3>Leaderboard and helpers</H3>
        <UL>
          <LI>
            The leaderboard ranks the field on the game&rsquo;s playing handicap and opens on the
            game&rsquo;s basis (net or gross). Places decided on the cards are marked{' '}
            <strong>SP</strong>; places decided by a playoff or by you are marked <strong>PO</strong>.
          </LI>
          <LI>
            With a buy-in, places show during the round; the dollar amounts wait until every card is
            complete.
          </LI>
          <LI>
            An event&rsquo;s leaderboard is shared automatically at its address. For the clubhouse TV,
            open <strong>📺 TV view</strong> from the Live Leaderboard box on the Scoring tab (or{' '}
            <Btn>📺 TV</Btn> on the tee time card).
          </LI>
          <LI>
            To let pro shop staff run the desk for this one event, without access to your dashboard,
            create an event desk code in <Btn>Set up event</Btn> &rarr;{' '}
            <strong>Event desk access</strong>.
          </LI>
        </UL>
      </Step>

      <Step n={9} title="Check the cards, settle ties and finalize" when="When the last card is in">
        <H3>When the cards come in</H3>
        <UL>
          <LI>
            Check each player&rsquo;s signed card against what is in TTO. Scores entered on phones
            during the round are already there.
          </LI>
          <LI>
            Open Admin &rarr; <strong>⛳ Scoring</strong> &rarr; <strong>⛳ Post-Round Score Entry</strong>{' '}
            and choose the round. Each player has a tab, listed by last name, and his grid shows
            every hole already entered.
          </LI>
          <LI>
            <strong>To correct a hole</strong>, click it and type the right score, or X for a pick-up.
            Then click <Btn>Save [player]&rsquo;s Scores</Btn>, or <Btn>Save All Scores</Btn> after
            several. The leaderboard updates straight away.
          </LI>
          <LI>
            <strong>A player who didn&rsquo;t score on his phone:</strong> enter his whole card the
            same way.
          </LI>
          <LI>
            A blank hole <em>between</em> holes he played won&rsquo;t save: enter a score or an X.
          </LI>
          <LI>
            <strong>Already finalized?</strong> Click <Btn>↶ Reopen Round</Btn> in{' '}
            <Btn>🎮 Games</Btn> first, fix the card, then finalize again. Otherwise the results and
            any money don&rsquo;t change.
          </LI>
          <LI>
            Helpers at the event desk can do the same from its <strong>Scores</strong> tile.
          </LI>
        </UL>

        <H3>Ties and finalizing</H3>
        <UL>
          <LI>
            With <strong>Scorecard playoff</strong> or <strong>Hole handicap</strong>, TTO breaks ties
            for the places you pay on its own. Identical cards still need your call: they appear under{' '}
            <strong>Ties to decide</strong> in the game.
          </LI>
          <LI>
            With <strong>We&rsquo;ll decide</strong>, or after a real playoff, open{' '}
            <Btn>🎮 Games</Btn>, put the tied players in order with ▲ ▼, click{' '}
            <Btn>Use this order</Btn>, and save.
          </LI>
          <LI>
            Click <Btn>🏁 Finalize Round &amp; Save Settlements</Btn>. If a card is incomplete, TTO
            lists the player and lets you mark him DNF or wait for his scores.
          </LI>
          <LI>
            The statement shows the placings for each competition, any prizes, each player&rsquo;s
            net scorecard with his stroke holes, and how ties were decided. With a buy-in, TTO
            settles the money player to player and you can email each player his results. With
            Handled outside TTO, the results show the places only.
          </LI>
        </UL>
      </Step>

      <H2>What changes once scoring starts</H2>
      <UL>
        <LI>
          <strong>Game settings can still change</strong>, but change them only if you must: the
          leaderboard and any money follow the new settings.
        </LI>
        <LI>
          <strong>Scorecard codes</strong> work from the day before the round through the day after,
          and stop once every game on the round is finalized. Scanning a code after that opens the
          results.
        </LI>
      </UL>

      <FAQ id="stroke-play-setup" faqs={FAQS} />
      <div className="guide-no-print">
        <GuideCTA />
      </div>
    </article>
  );
}
